import { useAddress } from '@/services'
import { toObject } from '@/utils'
import { map, pipe, head, find, equals, props, mergeAll, join } from 'ramda'
import { prop } from 'styled-tools'
import usePlacesAutocomplete, {
  HookArgs,
  Suggestion,
} from 'use-places-autocomplete'

interface GoogleAddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

type AddressField =
  | 'route'
  | 'street_number'
  | 'locality'
  | 'country'
  | 'postal_code'

type AddressKeys = 'street' | 'streetNr' | 'city' | 'country' | 'postalCode'

type ParsedAddress = {
  street: string
  streetNr: string
  city: string
  country: string
  postalCode: string
  googleLocation: string
}

export const usePlaces = (country: string) => {
  const params: HookArgs = country
    ? {
        requestOptions: { componentRestrictions: { country } },
        cache: false,
      }
    : {}

  return usePlacesAutocomplete(params)
}

export const makeOptions = map(
  ({ place_id: placeId, description }: Suggestion) => ({
    value: placeId,
    text: description,
  }),
)

export const formatValue = pipe(props(['street', 'streetNr']), join(' '))

const transform = (addressComponents: GoogleAddressComponent[]) => (
  type: AddressField,
  key: AddressKeys,
) =>
  pipe(
    find(pipe(prop('types'), head, equals(type))),
    prop('long_name'),
    toObject(key),
  )(addressComponents)

//@ts-expect-error fix after removing ramda
const makeAddressDetail = pipe(head, prop('address_components'), transform)

const getGoogleLocation = pipe(
  head,
  prop('formatted_address'),
  toObject('googleLocation'),
)

const makeDetails = (
  gapiResult: google.maps.GeocoderResult[],
): ParsedAddress => {
  const getDetail = makeAddressDetail(gapiResult)

  return mergeAll([
    //@ts-expect-error fix after removing ramda
    getDetail('route', 'street'),
    //@ts-expect-error fix after removing ramda
    getDetail('street_number', 'streetNr'),
    //@ts-expect-error fix after removing ramda
    getDetail('locality', 'city'),
    //@ts-expect-error fix after removing ramda
    getDetail('country', 'country'),
    //@ts-expect-error fix after removing ramda
    getDetail('postal_code', 'postalCode'),
    getGoogleLocation(gapiResult),
  ]) as ParsedAddress
}

export const useParseAddress = ({
  onSuccess,
}: {
  onSuccess: (parsed: ParsedAddress) => void
}) =>
  useAddress({
    onSuccess: (result: google.maps.GeocoderResult[]) => {
      onSuccess(makeDetails(result))
    },
  })
